import { useEffect, useRef, useState } from 'react';
import { useStore } from '../../../utils/store';
import Constants from '../../../utils/constants';
import HttpService from '../../../services/HttpService';
import { useSearch } from '../../../components/searchContext';
import { Box, Button, Paper, Typography } from '@mui/material';
import AddButtonComponent from '../../../components/form/AddButtonComponent';
import CustomPaginationActionsTable from '../../../components/TablePagination';
import SimpleDialogComponent from '../../../components/SimpleDialogComponent';
import AlertMessageComponent from '../../../components/AlertMessageComponent';
import SWMSList from './SWMSList';
import PaginateDataModel from '../../../Models/PaginateDataModel';
import SimpleSearchComponent from '../../../components/SimpleSearchComponent';
import { useAlert } from '../../../components/alertContext';
import ButtonComponent from '../../../components/form/ButtonComponent';
import { downloadFile, handleErrorMessage } from '../../../utils/storageUtil';

interface ProjectSWMSListProps {
  projectInternalId: number;
}
const ProjectSWMSList: React.FC<ProjectSWMSListProps> = ({
  projectInternalId,
}) => {
  const { searchQuery } = useSearch();
  const isInitialLoad = useRef(true);
  const { getAdmin } = useStore();
  const [searchQuerySWMS, setSearchQuerySWMS] = useState<any>('');
  const [totalRecordsSWMS, setTotalRecordsSWMS] = useState<number>(0);

  const [selectedSWMSList, setSelectedSWMSList] = useState<any>([]);
  const [selectedSWMSListIds, setSelectedSWMSListIds] = useState<any>([]);
  const [paginateSWMSList, setPaginateSWMSList] = useState<PaginateDataModel>({
    limit: Constants.DEFAULT_PAGE_SIZE,
    offset: 0,
  });
  const columns: any[] = [
    {
      field: 'id',
      headerName: 'Method',
      renderCell: (value: any, row: any) => row.swms.method,
    },
    {
      renderCell: (value: any, row: any) => row.swms.hazards,
      headerName: 'Hazards',
    },
    {
      renderCell: (value: any, row: any) => row.swms.risks,
      headerName: 'Risk',
    },
    {
      renderCell: (value: any, row: any) => row.swms.controlMeasures,
      headerName: 'Control Measures',
    },
    {
      renderCell: (value: any, row: any) => row.swms.revisedRiskRating,
      headerName: 'Revised Risk Rating',
    },
    {
      renderCell: (value: any, row: any) => row.swms.responsiblePerson,
      headerName: 'Responsible Person',
    },
  ];

  const [paginate, setPaginate] = useState<any>({
    limit: Constants.DEFAULT_PAGE_SIZE,
    offset: 0,
  });
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [versionNumber, setVersionNumber] = useState<string>('');
  const { alert, setAlert, handleCloseAlert } = useAlert();
  const [addNewDialogOpen, setAddNewDialogOpen] = useState<boolean>(false);
  const [inProgress, setInProgress] = useState<boolean>(false);
  const [loadingPDF, setLoadingPDF] = useState<boolean>(false);
  const [signedWarningDialogOpen, setSignedWarningDialogOpen] =
    useState<boolean>(false);
  const [rows, setRows] = useState<any>([]);
  const handleSelectedIds = async (id: any) => {
    console.log(id);
    setSelectedSWMSListIds((prevItems: any) => {
      const newItems = [...prevItems];
      if (!newItems.includes(id)) {
        newItems.push(id);
      } else {
        newItems.splice(newItems.indexOf(id), 1);
      }
      return newItems;
    });
  };

  /* SWMS List Operations Starts Here */
  const fetchSWMSList = async () => {
    try {
      // console.log(;)
      const response = await HttpService.get(
        `${Constants.apiURL}${Constants.swmsURL}`,
        { headers: { Authorization: `Bearer ${getAdmin()}` } },
        {
          search: searchQuerySWMS,
          limit: paginateSWMSList.limit,
          offset: paginateSWMSList.offset,
          isActive: 1,
        }
      );
      if (response.success) {
        setRows(response.data.paginateRecords);
        setTotalRecords(response.data.total);
      }
      //console.log('GET Response:', response);
    } catch (error: any) {
      console.error(`GET ERROR:`, error);
      setAlert({
        type: 'error',
        message: handleErrorMessage(error),
        open: true,
      });
    }
  };

  const clearForm = () => {
    setAlert({
      type: 'error',
      message: '',
      open: false,
    });
    setAddNewDialogOpen(false);
    fetchData();
  };

  const handleSaveSWMS = async () => {
    try {
      const response = await HttpService.post(
        `${Constants.apiURL}${Constants.projectDashboardsURL}/${projectInternalId}/swms`,
        { swmsIds: selectedSWMSListIds },
        { headers: { Authorization: `Bearer ${getAdmin()}` } }
      );
      if (response.success) {
        fetchData();
        clearForm();
      }
      //    console.log('GET Response:', response);
    } catch (error: any) {
      console.error(`GET ERROR:`, error);
      setAlert({
        type: 'error',
        message: handleErrorMessage(error),
        open: true,
      });
    }
  };
  const SignedWarningDialogContent = () => {
    return (
      <Box className="row mb-3 mt-2" sx={{ textAlign: 'right' }}>
        <Box sx={{ width: '450px' }}>
          <Box
            sx={{
              mb: 2,
              mt: 2,
              textAlign: 'left',
              fontWeight: 500,
              fontSize: '18px',
            }}
          >
            Are you sure you want to create a new version of this SWMS? This
            will archive this version including any signatures.
          </Box>
        </Box>
        <Button
          color="primary"
          sx={{ color: '#fff !important', mt: 3, mb: 3, textAlign: 'right' }}
          variant="contained"
          onClick={() => {
            setSignedWarningDialogOpen(false);
            setAddNewDialogOpen(true);
          }}
        >
          Add
        </Button>
      </Box>
    );
  };
  const AddDialogContent = () => {
    return (
      <Box
        className="row mb-3 mt-2"
        sx={{ textAlign: 'right', width: '1250px' }}
      >
        <Typography component={'h1'} sx={{ mt: 4, mb: 4, textAlign: 'left' }}>
          Add SWMS
        </Typography>
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
              mt: 2,
            }}
          >
            <SimpleSearchComponent
              onSearch={(event: any) => {
                setSearchQuerySWMS(event.target.value);
              }}
              search={searchQuerySWMS}
            />
          </Box>
          <SWMSList
            handleStatus={handleStatus}
            recordList={rows}
            selectedRecords={selectedSWMSListIds}
            totalRecords={totalRecords}
            paginate={paginateSWMSList}
            handlePagination={handlePaginationSWMS}
          ></SWMSList>
        </Box>
        <Button
          color="primary"
          sx={{ color: '#fff !important', mt: 3, mb: 3, textAlign: 'right' }}
          variant="contained"
          onClick={handleSaveSWMS}
        >
          Save
        </Button>
      </Box>
    );
  };

  const handleStatus = (row: any) => {
    handleSelectedIds(row.id);
  };

  const handlePagination = (paginationData: any) => {
    console.log(paginationData);
    setPaginate(paginationData);
  };
  const handlePaginationSWMS = (paginationData: any) => {
    console.log(paginationData);
    setPaginateSWMSList(paginationData);
  };
  const fetchData = async () => {
    try {
      const response = await HttpService.get(
        `${Constants.apiURL}${Constants.projectDashboardsURL}/${projectInternalId}/swms`,
        { headers: { Authorization: `Bearer ${getAdmin()}` } },
        {
          search: searchQuery,
          limit: paginate.limit,
          offset: paginate.offset,
          isActive: 1,
        }
      );
      if (response.data) {
        if (response.data.SWMSCurrentVersion) {
          const projectSwmsData = response.data.SWMSCurrentVersion.projectSWMS;
          setVersionNumber(response.data.SWMSCurrentVersion.version);
          setSelectedSWMSList(projectSwmsData);
          setTotalRecordsSWMS(projectSwmsData.length);
          const idArray = projectSwmsData.map((item: any) => item.swms.id);
          setSelectedSWMSListIds(idArray);
        }
      }
      //console.log('GET Response:', response);
    } catch (error: any) {
      console.error(`GET ERROR:`, error);
      setAlert({
        type: 'error',
        message: handleErrorMessage(error),
        open: true,
      });
    }
  };
  const fetchSignedFlag = async () => {
    try {
      setInProgress(true);
      const response = await HttpService.get(
        `${Constants.apiURL}${Constants.projectDashboardsURL}/${projectInternalId}/swms/signed`,
        { headers: { Authorization: `Bearer ${getAdmin()}` } },
        {
          search: searchQuery,
          limit: paginate.limit,
          offset: paginate.offset,
          isActive: 1,
        }
      );
      setInProgress(false);
      if (response.data) {
        setSignedWarningDialogOpen(true);
      } else {
        setAddNewDialogOpen(true);
      }
      //console.log('GET Response:', response);
    } catch (error: any) {
      setInProgress(false);
      console.error(`GET ERROR:`, error);
      setAlert({
        type: 'error',
        message: handleErrorMessage(error),
        open: true,
      });
    }
  };
  const ExportPDF = async () => {
    try {
      setLoadingPDF(true);
      const response = await HttpService.get(
        `${Constants.apiURL}/pdf/projectSWMS/${projectInternalId}`,
        {
          headers: {
            Authorization: `Bearer ${getAdmin()}`,
            Accept: 'application/pdf',
          },

          responseType: 'blob',
        }
      );
      setLoadingPDF(false);
      downloadFile(response, 'SWMS_Project_' + projectInternalId + '.pdf');
      // console.log('GET Response:', response);
    } catch (error: any) {
      setLoadingPDF(false);
      console.error(`GET ERROR:`, error);
      setAlert({
        type: 'error',
        message: handleErrorMessage(error),
        open: true,
      });
    }
  };

  useEffect(() => {
    if (paginate || isInitialLoad.current) {
      fetchSWMSList();
      fetchData();
    }
    isInitialLoad.current = false;
  }, [searchQuery, paginate, paginateSWMSList]);

  return (
    <div className="containergrid">
      <div className="mt-2">
        <Paper sx={{ width: '100%' }} className="mt-4">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px',
              mb: 2,
            }}
          >
            <Typography variant="h6">SWMS</Typography>
            <Box>
              {versionNumber && (
                <span style={{ marginRight: '15px' }}>
                  {'Current Version ' + versionNumber}{' '}
                </span>
              )}
              <Button
                sx={{
                  color: '#000',
                  cursor: loadingPDF ? 'not-allowed' : 'pointer',
                  marginRight: '20px',
                }}
                type="button"
                variant="contained"
                color={'secondary'}
                onClick={() => {
                  ExportPDF();
                }}
              >
                {loadingPDF ? 'Exporting...' : 'Export Pdf'}
              </Button>
              <AddButtonComponent
                onClick={() => {
                  fetchSignedFlag();
                }}
                keyName="checkIcon"
                label={inProgress ? 'Processing...' : 'Add SWMS'}
              />
            </Box>
          </Box>
          <CustomPaginationActionsTable
            rows={selectedSWMSList}
            columns={columns}
            totalRecords={totalRecordsSWMS}
            handlePagination={handlePagination}
            handleRowClick={(row) => {
              console.log('row-clicked');
              //   navigate('/data-management/labour/' + row.id);
              //console.log(data);
            }}
            paginationModel={{
              page: paginate.offset,
              pageSize: paginate.limit,
            }}
            handleStatus={() => {
              console.log('handlestatusmain');
            }}
          />
          <SimpleDialogComponent
            title={''}
            dialogContent={AddDialogContent()}
            open={addNewDialogOpen}
            handleClose={() => {
              setAddNewDialogOpen(false);
            }}
          />
          <SimpleDialogComponent
            title={'Confirmation'}
            dialogContent={SignedWarningDialogContent()}
            open={signedWarningDialogOpen}
            handleClose={() => {
              setSignedWarningDialogOpen(false);
            }}
          />
          <AlertMessageComponent
            type={alert.type}
            open={alert.open}
            handleClose={handleCloseAlert}
            message={alert.message}
          />
        </Paper>
      </div>
    </div>
  );
};

export default ProjectSWMSList;
